import styled from "@emotion/styled";
import { Stack, Typography } from "@mui/material";
import { type DateView, DatePicker as MuiDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { BaseDateValidationProps } from "@mui/x-date-pickers/internals";
import { ReactComponent as CalendarIcon } from "Components/_Shared/Icons/Assets/calendar.svg";
import { StyledFormInput as BaseStyledFormInput, StyledFormControl, StyledValidationText } from "Forms/Components/FormStyles";
import { useFieldError } from "Forms/Hooks/useFieldError";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { Spacing } from "Infrastructure/Themes/Themes";
import { format, startOfMonth } from "date-fns";
import { isValid, parseISO } from "date-fns";
import { get } from "lodash-es";
import type { JSX } from "react";
import { type Control, Controller, type FieldErrors, type FieldPath, type FieldValues } from "react-hook-form";

const StyledLabel = styled.label<{ $disabled: boolean }>`
  color: ${(props) => (props.$disabled ? props.theme.palette.text.disabled : props.theme.palette.text.primary)};
`;

type NewType<FormType extends FieldValues> = {
  control: Control<FormType, object>;
  label?: string | JSX.Element;
  name: FieldPath<FormType>;
  errors: FieldErrors<FormType>;
  disablePast?: boolean;
  disableFuture?: boolean;
  disabled?: boolean;
  onChange?: (date: Date | null) => void;
  onBlur?: () => void;
  labelEndAdornment?: JSX.Element;
  // Without "day" the picker selects a month and the value is always the 1st of it.
  views?: DateView[];
  ariaDescribedBy?: string;
};

export type Props<FormType extends FieldValues> = NewType<FormType> & BaseDateValidationProps<Date>;

const StyledFormInput = styled(BaseStyledFormInput)<{
  $hasError: boolean;
}>`
  .MuiInputBase-formControl {
    background-color: ${(props) => props.theme.palette.common.white};
  }

  .MuiInputBase-root {
    border: 1px solid ${(props) => (props.$hasError ? props.theme.palette.error.main : props.theme.colors.border)};
    border-radius: ${(props) => props.theme.spacing(Spacing.M)} !important;
    padding: ${(props) => props.theme.spacing(Spacing.M, Spacing.L)} !important;
    height: 40px;
  }

  .MuiInputBase-input {
    padding: 0px;
    box-sizing: border-box;
    color: ${(props) => props.theme.colors.textPrimary};
    font-size: 16px;
    font-weight: 400;
    line-height: 1;

    ::placeholder {
      color: ${(props) => props.theme.colors.textPrimary} !important;
    }
  }

  .MuiOutlinedInput-multiline {
    padding: 0;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .Mui-disabled {
    &.MuiInputBase-formControl {
      background-color: ${(props) => props.theme.colors.primaryDisabled};

      .MuiInputBase-input {
        color: ${(props) => props.theme.palette.text.primary}40;
      }
    }
  }
`;

export const DatePicker = <T extends object>(props: Props<T>) => {
  const {
    label,
    name,
    errors,
    disablePast,
    disableFuture,
    disabled,
    control,
    labelEndAdornment,
    minDate,
    views,
    ariaDescribedBy,
    ...rest
  } = props;
  const isMonthPicker = !!views && !views.includes("day");

  const fieldError = get(errors, name as string);
  const { hasError, errorMessage } = useFieldError({ fieldError });
  const locale = useDateFnsLocale();

  const handleChange = (value: Date | null, onChange: (...event: any[]) => void) => {
    const { onChange: onChangeProp } = rest;
    if (value === null) {
      onChange(null);
      onChangeProp?.(null);
      return;
    }
    if (isValid(value)) {
      const date = isMonthPicker ? startOfMonth(value) : value;
      onChange(format(date, "yyyy-MM-dd"));
      onChangeProp?.(date);
    }
  };

  const handleBlur = (onBlur: () => void) => {
    const { onBlur: onBlurProp } = rest;
    onBlur();
    onBlurProp?.();
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, onBlur, ref } }) => {
        return (
          <StyledFormControl>
            {!!label && (
              <StyledLabel htmlFor={name} $disabled={disabled ?? false}>
                <Stack direction={"row"} gap={1}>
                  <Typography fontWeight={500} variant="body2" mb={Spacing.S}>
                    {label}
                  </Typography>
                  {labelEndAdornment}
                </Stack>
              </StyledLabel>
            )}
            <StyledFormInput $hasError={hasError}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                <MuiDatePicker
                  views={views}
                  format={isMonthPicker ? "MM/yyyy" : "dd. MM. yyyy"}
                  value={typeof value === "string" ? (isValid(parseISO(value)) ? parseISO(value) : null) : isValid(value) ? value : null}
                  onChange={(value) => handleChange(value, onChange)}
                  onClose={() => handleBlur(onBlur)}
                  disableFuture={disableFuture}
                  minDate={minDate}
                  disablePast={disablePast}
                  disabled={disabled}
                  slots={{
                    openPickerIcon: () => <CalendarIcon />,
                  }}
                  slotProps={{
                    textField: {
                      inputRef: ref,
                      inputProps: ariaDescribedBy ? { "aria-describedby": ariaDescribedBy } : undefined,
                      onBlur: () => handleBlur(onBlur),
                      sx: { maxWidth: "md" },
                    },
                  }}
                />
              </LocalizationProvider>
            </StyledFormInput>
            {hasError && <StyledValidationText>{errorMessage}</StyledValidationText>}
          </StyledFormControl>
        );
      }}
    />
  );
};
